import React, { useEffect, useState } from 'react';
import { Amplify } from 'aws-amplify';
import {
  signIn,
  signOut,
  getCurrentUser,
  AuthUser,
  signInWithRedirect,
  fetchAuthSession,
  fetchUserAttributes,
} from 'aws-amplify/auth';

import { AuthContext, type AuthContextType } from './AuthContextBase';
import {
  getUserProfile,
  clearUserProfile,
} from '../services/userProfileService';
import type { UserProfile } from '../types/profile';

interface AuthProviderProps {
  children: React.ReactNode;
}

import { getCognitoConfig } from './authConfig';

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [firstName, setFirstName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const config = getCognitoConfig();

    // Always check for localStorage profile first (for regular users)
    const profile = getUserProfile();
    if (profile) {
      setUserProfile(profile);
      setIsAuthenticated(true);
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }

    // Configure Cognito for admin users only
    if (config) {
      Amplify.configure({
        Auth: {
          Cognito: {
            userPoolId: config.userPoolId,
            userPoolClientId: config.userPoolClientId,
            identityPoolId: config.identityPoolId,
          },
        },
      });

      // Check if user is already authenticated via Cognito (admin users)
      checkAuthState();
    }
  }, []);

  const checkAuthState = async () => {
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
      // Don't override profile-based authentication
      if (!userProfile) {
        setIsAuthenticated(true);
      }

      // Fetch user attributes to get firstName
      try {
        const attributes = await fetchUserAttributes();
        setFirstName(attributes.given_name || attributes.name || null);
      } catch (attrError) {
        console.warn('Could not fetch user attributes:', attrError);
      }
    } catch (error) {
      console.log('No Cognito user authenticated');
      // This is fine - regular users don't use Cognito
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (username: string, password: string) => {
    try {
      setIsLoading(true);
      await signIn({ username, password });
      await checkAuthState();
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      setIsLoading(true);

      // Clear localStorage profile
      clearUserProfile();
      setUserProfile(null);

      // Sign out from Cognito if applicable
      if (user) {
        await signOut();
        setUser(null);
      }

      setFirstName(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const reloadProfile = () => {
    const profile = getUserProfile();
    if (profile) {
      setUserProfile(profile);
      setIsAuthenticated(true);
      return true;
    }
    return false;
  };

  const loginWithRedirect = async () => {
    try {
      await signInWithRedirect({ provider: { custom: 'Cognito' } });
    } catch (error) {
      console.error('Redirect login error:', error);
      throw error;
    }
  };

  const getCredentials = async () => {
    try {
      const session = await fetchAuthSession();
      return session.credentials;
    } catch (error) {
      console.error('Error getting credentials:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    userProfile,
    firstName,
    isLoading,
    isAuthenticated,
    login,
    logout,
    loginWithRedirect,
    getCredentials,
    reloadProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
